import { useCallback, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { FlatArtifactGrid, GroupedArtifactGrid } from './artifact-grid';
import { groupArtifacts } from './utils';
import { VersionsDialog } from './versions-dialog';
import { APIBaseChronos } from '@/api/hosts';
import { useTogglePublishedArtifact } from '@/api/mutations/useTogglePublishedArtifact';
import { useGetMyKimArtifacts, useGetPublishedKimArtifacts } from '@/api/queries/useGetKimArtifacts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { PageTitle } from '@/new/components/page-title';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

export function KimArtifacts() {
  const { matterId, subsetId } = useParams();
  const { data: myArtifacts, isLoading: myLoading } = useGetMyKimArtifacts(matterId, subsetId);
  const { data: publishedArtifacts, isLoading: publishedLoading } = useGetPublishedKimArtifacts(matterId, subsetId);
  const {
    mutate: togglePublished,
    isLoading: isToggling,
    variables: togglingVariables,
  } = useTogglePublishedArtifact(matterId, subsetId);
  const togglingKey =
    isToggling && togglingVariables ? `${togglingVariables.artifactId}-${togglingVariables.versionNumber}` : null;
  const { getFetchConfig } = useGetFetchConfig();

  const [versionsArtifactId, setVersionsArtifactId] = useState<string | null>(null);

  const myGroups = useMemo(() => groupArtifacts(myArtifacts), [myArtifacts]);

  // Keep versions dialog in sync with latest query data
  const versionsGroup = useMemo(() => {
    if (!versionsArtifactId) return null;
    return myGroups.find((g) => g.latest.id === versionsArtifactId) ?? null;
  }, [versionsArtifactId, myGroups]);

  const allArtifacts = useMemo(
    () => [...(myArtifacts ?? []), ...(publishedArtifacts ?? [])],
    [myArtifacts, publishedArtifacts],
  );

  const handleDownload = useCallback(
    async (artifactId: string, versionNumber: number) => {
      const artifact = allArtifacts.find((a) => a.id === artifactId && a.versionNumber === versionNumber);
      if (!artifact) {
        toast.error('Artifact not found');
        return;
      }

      try {
        const fetchConfig = getFetchConfig({
          method: FetchMethod.POST,
          data: {
            title: artifact.name,
            messageId: artifact.messageId,
            artifactId,
            artifactVersionNumber: versionNumber,
          },
        });

        const res = await fetch(
          `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${artifact.message.threadId}/export`,
          fetchConfig,
        );

        if (!res.ok) throw new Error('Download failed');

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${artifact.name}.docx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch {
        toast.error('Failed to download artifact');
      }
    },
    [allArtifacts, getFetchConfig, matterId, subsetId],
  );

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-8 py-5">
        <div className="my-12">
          <PageTitle text="Work Product" />
        </div>

        <Tabs defaultValue="mine" className="mt-4 mb-32">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="mine">Your Work Product</TabsTrigger>
            <TabsTrigger value="published">Published</TabsTrigger>
          </TabsList>

          <TabsContent value="published" className="mt-8">
            <FlatArtifactGrid
              artifacts={publishedArtifacts}
              isLoading={publishedLoading}
              emptyMessage="No published work product yet in this Subset."
              onTogglePublished={togglePublished}
              onDownload={handleDownload}
              togglingKey={togglingKey}
            />
          </TabsContent>

          <TabsContent value="mine" className="mt-8">
            <GroupedArtifactGrid
              groups={myGroups}
              isLoading={myLoading}
              emptyMessage="No work product yet in this Subset."
              onTogglePublished={togglePublished}
              onDownload={handleDownload}
              onShowVersions={(group) => setVersionsArtifactId(group.latest.id)}
              togglingKey={togglingKey}
            />
          </TabsContent>
        </Tabs>
      </div>

      <VersionsDialog
        group={versionsGroup}
        onClose={() => setVersionsArtifactId(null)}
        onTogglePublished={togglePublished}
        onDownload={handleDownload}
        togglingKey={togglingKey}
      />
    </div>
  );
}
